import { useMemo } from "react";

import { ITeachingMaterial } from "../state";
import { TEACHING_MATERIAL_KEYWORDS } from "../constants/teaching_materials";
import { CategorizedMaterials } from "../libs/teachingMaterialUtils";

type MaterialForCategorization = Pick<ITeachingMaterial, "kind" | "title">;

const useCategorizeMaterials = <T extends MaterialForCategorization>(
  materials: T[],
): CategorizedMaterials<T> => {
  return useMemo(() => {
    const materialsExcludingOrientation = materials.filter(
      m => !m.title.includes(TEACHING_MATERIAL_KEYWORDS.ORIENTATION),
    );
    const coreMaterials = materialsExcludingOrientation.filter(m =>
      (m.kind || "").includes(TEACHING_MATERIAL_KEYWORDS.CORE),
    );
    const middleSchoolCoreMaterials = coreMaterials.filter(m =>
      m.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE),
    );
    const highSchoolCoreMaterials = coreMaterials.filter(m =>
      m.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH),
    );
    const otherCoreMaterials = coreMaterials.filter(
      m =>
        !m.title.includes(TEACHING_MATERIAL_KEYWORDS.MIDDLE) &&
        !m.title.includes(TEACHING_MATERIAL_KEYWORDS.HIGH),
    );
    const baseMaterials = materialsExcludingOrientation.filter(m =>
      (m.kind || "").includes(TEACHING_MATERIAL_KEYWORDS.BASE),
    );
    const hasMiddle = middleSchoolCoreMaterials.length > 0;
    const hasHigh = highSchoolCoreMaterials.length > 0;
    const hasCore = otherCoreMaterials.length > 0;
    const hasBase = baseMaterials.length > 0;
    const hasMultipleMaterialCategories =
      [hasMiddle, hasHigh, hasCore, hasBase].filter(Boolean).length > 1;
    return {
      middle: middleSchoolCoreMaterials,
      high: highSchoolCoreMaterials,
      core: otherCoreMaterials,
      base: baseMaterials,
      hasMiddle,
      hasHigh,
      hasCore,
      hasBase,
      hasMultipleMaterialCategories,
    };
  }, [materials]);
};

export default useCategorizeMaterials;
